import { ANGLE_STEPS, iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { cellAtWorld, cellCenter, findPath, heroWalkable } from '../grid.ts';
import type { BattleState, CamsRules, Enemy, TenazaState } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { inArc, turnOf } from './golems.ts';

/**
 * La Tenaza (docs/sendas/artifice.md, chapter 3, act 3, the boss of the test): a slag crab the size of a cart, its shell
 * of cooled lava, embers in its joints. It comes out of the river `out` into the fight and goes for the trip-hammer's
 * wheel; with the hero within `chase` (or for a while after it let go of the wheel) it goes for the hero instead. Slow,
 * it turns slowly; it walks only forward. Its pincers open `warn` (3 s, its aim held) before they snap shut in an arc in
 * front of it (`heroBp` of the hero's life, `blows` of a person's). At the wheel it grabs it for `grip`: the shaft
 * still (no turn of the hammer can start, a turn under way stops where it is), then lets go and goes for the hero
 * `again`. Struck within `front` of its face, a blow lands at `frontBp`: the pincers cover it; from the sides, whole
 * (gripping the wheel it shows them). Its splash (`splash`: its claws in the river, warned 3 s) reaches the whole bank,
 * nowhere out of it: its fight can't be long.
 *
 * The crab is a nightmare of the fight (an ogre in the sim, drawn as La Tenaza: the render reads `s.senda.cams.tz`);
 * this module moves it (`tenazaHold`, from the enemies' tick) and reads every blow on it (`tenazaTakes`, from combat).
 *
 * Cost: one crab; it looks at the hero once a tick; a way round a rock is looked for at most once a second.
 */

const norm = (a: number): number => ((a % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS;

export const tenazaFresh = (r: CamsRules): TenazaState => ({ id: 0, mode: 0, wind: -1, aim: 0, next: 0, grip: -1, again: 0, splash: r.boss.splash.first, splashAt: -1, step: -1, stepAt: -100, pend: [], down: -1 });

/** Whether an enemy is La Tenaza. */
export const tenazaOf = (s: BattleState, id: number): boolean => !!id && s.senda?.cams?.tz.id === id;

/** La Tenaza has the wheel: the shaft is still. */
export const tenazaGrips = (s: BattleState): boolean => s.senda?.cams?.tz.mode === 2;

/** Its coming out, its letting go of the wheel, its splash, its fall (from the act's tick). */
export const tenazaTick = (s: BattleState, r: CamsRules, tz: TenazaState, cue: (c: string) => void): void => {
  const B = r.boss;
  if (tz.pend.length) {
    for (const c of tz.pend) cue(c);
    tz.pend.length = 0;
  }
  if (tz.mode === 3) return;
  if (tz.mode === 0) {
    if (s.waveT < B.out) return;
    const e = spawnEnemy(s, 'ogre', 1, false, 0, B.at.x, B.at.y, B.hp);
    e.facing = iatan2(r.wheel.y - e.y, r.wheel.x - e.x);
    e.anim = 'idle';
    tz.id = e.id;
    tz.mode = 1;
    tz.next = s.tick + 24;
    ev(s, { t: 'senda', kind: 'tzOut', npc: '', x: e.x, y: e.y });
    cue('out');
    return;
  }
  const e = s.enemies.find((x) => x.id === tz.id);
  if (!e || e.hp <= 0) {
    tz.mode = 3;
    tz.down = s.tick;
    tz.wind = -1;
    tz.splashAt = -1;
    ev(s, { t: 'senda', kind: 'tzDown', npc: '', x: e?.x ?? B.at.x, y: e?.y ?? B.at.y });
    cue('bossDown');
    return;
  }
  if (tz.mode === 2 && s.tick >= tz.grip) {
    tz.mode = 1;
    tz.again = s.tick + B.again;
    tz.next = s.tick + 12;
    ev(s, { t: 'senda', kind: 'tzLet', npc: '', x: e.x, y: e.y });
    cue('let');
  }
  // The splash: its claws down in the river, then the whole bank.
  if (tz.splashAt < 0 && s.waveT >= tz.splash) {
    tz.splashAt = s.tick + B.splash.warn;
    ev(s, { t: 'senda', kind: 'tzSplashWarn', npc: '', x: e.x, y: e.y, n: B.splash.warn });
    cue('splash');
  } else if (tz.splashAt >= 0 && s.tick >= tz.splashAt) {
    tz.splashAt = -1;
    tz.splash = s.waveT + B.splash.every - B.splash.warn;
    ev(s, { t: 'senda', kind: 'tzSplash', npc: '', x: e.x, y: e.y });
    for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, B.splash.heroBp), e, 'fire', true);
    for (const n of s.senda?.npcs ?? []) {
      if (n.hp <= 0 || n.away) continue;
      n.hp = imax(0, n.hp - B.splash.blows);
      ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    }
  }
};

/** Turns `e` toward an angle by at most `turn` a tick. */
const turnToward = (e: Enemy, to: number, turn: number): void => {
  const d = turnOf(e.facing, to);
  e.facing = norm(e.facing + (d > turn ? turn : d < -turn ? -turn : d));
};

/** Where to walk toward (x, y): straight if the next step is floor, else the first cell of a way round (once a second). */
const stepTo = (s: BattleState, tz: TenazaState, e: Enemy, x: number, y: number): { x: number; y: number } => {
  const dx = x - e.x;
  const dy = y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  if (heroWalkable(s, cellAtWorld(s, e.x + idiv(dx * 700, d), e.y + idiv(dy * 700, d)))) return { x, y };
  if (s.tick - tz.stepAt >= 12 || tz.step < 0) {
    tz.stepAt = s.tick;
    const way = findPath(s, cellAtWorld(s, e.x, e.y), cellAtWorld(s, x, y));
    tz.step = way[0] ?? -1;
  }
  return tz.step >= 0 ? cellCenter(s.map.radius, tz.step) : { x, y };
};

/** The pincers snap: what is in the arc in front of it. */
const snap = (s: BattleState, r: CamsRules, tz: TenazaState, e: Enemy): void => {
  const B = r.boss;
  ev(s, { t: 'senda', kind: 'tzSnap', npc: '', x: e.x, y: e.y, n: tz.aim });
  e.anim = 'attack';
  e.animT = 0;
  for (const h of s.heroes) if (h.downTicks === 0 && inArc(e.x, e.y, tz.aim, B.reach, B.arc, h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, B.heroBp), e, 'physical', true);
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away || !inArc(e.x, e.y, tz.aim, B.reach, B.arc, n.x, n.y)) continue;
    n.hp = imax(0, n.hp - B.blows);
    n.flinch = 12;
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** La Tenaza's move (from the enemies' tick; true: it's the crab, nothing else moves it). */
export const tenazaHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.cams;
  const tz = s.senda?.cams?.tz;
  if (!r || !tz || tz.id !== e.id) return false;
  const B = r.boss;
  if (tz.mode === 3) return true;
  if (tz.wind >= 0) {
    e.facing = tz.aim;
    e.anim = 'cast';
    if (s.tick >= tz.wind) {
      snap(s, r, tz, e);
      tz.wind = -1;
      tz.next = s.tick + B.rest;
    }
    return true;
  }
  if (e.anim === 'attack' && e.animT > 8) e.anim = 'idle';
  if (tz.mode === 2) {
    // On the wheel: its claws round it, its sides open to the bank.
    e.facing = iatan2(r.wheel.y - e.y, r.wheel.x - e.x);
    e.anim = 'cast';
    return true;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The hero in front and within its reach: the pincers open.
  if (h && s.tick >= tz.next && inArc(e.x, e.y, e.facing, B.reach - 150, 100, h.x, h.y)) {
    tz.wind = s.tick + B.warn;
    tz.aim = e.facing;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'senda', kind: 'tzWarn', npc: '', x: e.x, y: e.y, n: tz.aim });
    return true;
  }
  const chase = !!h && (s.tick < tz.again || withinRange(e.x, e.y, h.x, h.y, B.chase));
  const to = chase && h ? { x: h.x, y: h.y } : r.wheel;
  if (!chase && withinRange(e.x, e.y, r.wheel.x, r.wheel.y, B.gripR)) {
    tz.mode = 2;
    tz.grip = s.tick + B.grip;
    ev(s, { t: 'senda', kind: 'tzGrip', npc: '', x: r.wheel.x, y: r.wheel.y, n: B.grip });
    tz.pend.push('grip');
    return true;
  }
  const way = stepTo(s, tz, e, to.x, to.y);
  const want = iatan2(way.y - e.y, way.x - e.x);
  turnToward(e, want, B.turn);
  const off = turnOf(e.facing, want);
  // Close on the hero: turn to face it, no nearer.
  if (chase && h && withinRange(e.x, e.y, h.x, h.y, B.reach - 300)) {
    e.anim = 'idle';
    return true;
  }
  if (off > 170 || off < -170) {
    e.anim = 'idle';
    return true;
  }
  const dx = way.x - e.x;
  const dy = way.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const nx = e.x + idiv(dx * B.pace, d);
  const ny = e.y + idiv(dy * B.pace, d);
  if (heroWalkable(s, cellAtWorld(s, nx, ny))) {
    e.x = nx;
    e.y = ny;
  }
  e.anim = 'walk';
  return true;
};

/** A blow on La Tenaza (combat's damage): from in front of its face, at `frontBp` (its pincers cover it); else whole. */
export const tenazaTakes = (s: BattleState, e: Enemy, dmg: number, fromX: number, fromY: number): number => {
  const r = s.rules?.senda?.cams;
  const tz = s.senda?.cams?.tz;
  if (!r || !tz || tz.id !== e.id || tz.mode === 3 || (fromX === e.x && fromY === e.y)) return dmg;
  const d = turnOf(e.facing, iatan2(fromY - e.y, fromX - e.x));
  if (d > r.boss.front || d < -r.boss.front) return dmg;
  if (!tz.pend.includes('front')) tz.pend.push('front');
  if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
  return mulBp(dmg, r.boss.frontBp);
};
